import React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/shared/utils/shadcn";
import { map, takeRight } from "lodash";
import { useFeatureFlag } from "@/shared/hooks/useFeatureFlag";
import { FeatureFlagKey } from "@/types/feature-flags";

interface EvaluationRow {
  title: string;
  evaluation?: number;
}

interface EvaluationsSummaryTableProps {
  rows: EvaluationRow[];
  maxScore?: number;
}

export const EvaluationsSummaryTable: React.FC<
  EvaluationsSummaryTableProps
> = ({ rows }) => {
  const { t } = useTranslation();

  const evaluationLevelsLabels = [
    t("assessments.low"),
    t("assessments.emerging"),
    t("assessments.moderate"),
    t("assessments.high"),
    t("assessments.distinctive"),
  ];

  const hasFourLevels = useFeatureFlag(FeatureFlagKey.FOUR_POINT_SCORING);

  const evaluationLevels = map(
    takeRight(evaluationLevelsLabels, hasFourLevels ? 4 : 5),
    (label, index) => ({
      value: index + 1,
      label,
    })
  );

  return (
    <table className="w-full border-collapse border-b border-b-gray-300">
      <thead>
        <tr className="bg-blue-500! text-white text-sm">
          <th className="p-2 border !bg-blue-500 text-md px-4">
            Assessment Summary
          </th>
          {evaluationLevels.map((level) => (
            <th
              key={level.value}
              className="p-2 text-center border uppercase !bg-blue-500 w-[200px]"
            >
              {level.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.title}>
            <td className="p-4 border-y">{row.title}</td>
            {evaluationLevels.map((level) => (
              <td
                key={level.value}
                className="p-2 border-y border-l text-center"
              >
                <div
                  className={cn(
                    "inline-flex items-center justify-center w-8 h-8  rounded-full mx-auto",
                    row.evaluation === level.value && "bg-blue-500 text-white"
                  )}
                >
                  <span>{level.value}</span>
                </div>
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};
